import React, { useState } from "react";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", role: "student", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setSent(true);
    } catch {
      toast.error("Could not send. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Contact WriterSpark | Get in Touch with Our Team"
        description="Questions about WriterSpark? Reach our team at info@writersspark.net or use our contact form. We respond within 1-2 business days."
        path="/contact"
        keywords="contact WriterSpark, writing platform support, info@writersspark.net"
      />
      <PublicHeader />

      <section className="max-w-4xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <span className="text-xs uppercase font-bold tracking-widest text-indigo-700">Contact</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight" style={{fontFamily:'Outfit'}}>
            Let's talk about young writers.
          </h1>
          <p className="mt-6 text-slate-600 leading-relaxed">
            Whether you're a parent with a question, a teacher exploring classroom use, or a student with feedback — we read every message.
          </p>
          <a href="mailto:info@writersspark.net" data-testid="contact-email-link"
            className="mt-6 inline-flex items-center gap-3 rounded-2xl border-2 border-slate-200 bg-white px-5 py-4 hover:border-indigo-300 transition">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center"><Mail size={18}/></div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Email us</div>
              <div className="font-semibold text-slate-900">info@writersspark.net</div>
            </div>
          </a>
        </div>

        <div className="tactile-card p-7">
          {sent ? (
            <div data-testid="contact-success" className="py-12 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-4">
                <Send size={22}/>
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Thanks!</h2>
              <p className="text-slate-600">Your message is on its way. We typically reply within 1-2 business days.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Name</label>
                <input data-testid="contact-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                  className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none" />
              </div>
              <div>
                <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Email</label>
                <input data-testid="contact-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                  className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none" />
              </div>
              <div>
                <label className="text-xs uppercase font-bold tracking-widest text-slate-500">I am a...</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["student", "parent", "teacher", "other"].map(r => (
                    <button key={r} type="button" data-testid={`contact-role-${r}`}
                      onClick={()=>setForm({...form, role: r})}
                      className={`rounded-full px-4 py-2 text-sm font-bold border-2 capitalize ${form.role===r ? 'bg-indigo-600 text-white border-indigo-700':'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'}`}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Message</label>
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})}
                  className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-4 py-3 outline-none resize-none" />
              </div>
              <button data-testid="contact-submit" disabled={loading} type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full py-3.5 tactile-shadow disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="animate-spin" size={16}/> Sending…</> : <><Send size={16}/> Send message</>}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
